import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";

export class PdfInputError extends Error {
  constructor(message) {
    super(message);
    this.name = "PdfInputError";
  }
}

const MM = 2.834645669; // mm -> pt
const A4 = [595.28, 841.89];

const THERMAL = {
  "thermal-4x6": [288, 432],
  "thermal-100x150": [100 * MM, 150 * MM],
  "thermal-3x5": [216, 360],
  "thermal-100x100": [100 * MM, 100 * MM],
};

const COURIERS = [
  "Xpress Bees", "XpressBees", "Shadowfax", "Delhivery", "E-Kart Logistics",
  "Ekart", "E-Kart", "Ecom Express", "Blue Dart", "BlueDart", "DTDC",
];

// Crop regions as fractions of page size (origin bottom-left, y up).
// Flipkart values measured from a real Flipkart A4 label+invoice sample:
// the portrait label sits in the centre column, scaling uniformly to 900x1500.
const REGION = {
  flipkart: {
    label: { l: 0.303, r: 0.689, t: 0.985, b: 0.530 },
    invoice: { l: 0.02, r: 0.98, t: 0.52, b: 0.02 },
  },
  meesho: {
    // Measured from a real Meesho A4 label+invoice sample: label from
    // "Customer Address" (top) down to just above the "TAX INVOICE" strip.
    label: { l: 0.0, r: 1.0, t: 0.985, b: 0.60 },
    invoice: { l: 0.0, r: 1.0, t: 0.59, b: 0.0 },
  },
  amazon: {
    label: { l: 0.0, r: 1.0, t: 1.0, b: 0.0 },
    invoice: { l: 0.0, r: 1.0, t: 1.0, b: 0.0 },
  },
};

function resolvePageSize(pageSize) {
  if (!pageSize) return null;
  if (typeof pageSize === "string") {
    if (THERMAL[pageSize]) return THERMAL[pageSize];
    throw new PdfInputError("Unknown label size selected.");
  }
  const { widthMm, heightMm } = pageSize;
  if (!widthMm || !heightMm) throw new PdfInputError("Enter a valid custom label size.");
  for (const v of [widthMm, heightMm]) {
    if (v < 30 || v > 300) throw new PdfInputError("Custom label size must be between 30 and 300 mm.");
  }
  return [widthMm * MM, heightMm * MM];
}

function detectMarket(text) {
  const t = text.toLowerCase();
  // Meesho: the brand is often a logo image, so use layout-specific text signals.
  if (t.includes("meesho") || t.includes("check the payable amount") || (t.includes("destination code") && t.includes("return code"))) return "meesho";
  if (t.includes("shopsy")) return "flipkart";
  if (t.includes("flipkart")) return "flipkart";
  if (t.includes("amazon")) return "amazon";
  // Flipkart/Shopsy labels often show the brand as a logo image (no "Flipkart"
  // text); fall back to layout-specific text signals.
  if (/e-?kart|awb no|ordered through|fssai/.test(t)) return "flipkart";
  return null;
}

function findCourier(text) {
  const t = text.toLowerCase();
  for (const c of COURIERS) {
    if (t.includes(c.toLowerCase())) {
      return c.replace("XpressBees", "Xpress Bees").replace("BlueDart", "Blue Dart");
    }
  }
  return null;
}

function findPayment(text) {
  const t = text.toLowerCase();
  if (t.includes("prepaid")) return "Prepaid";
  if (t.includes("cod") || t.includes("cash on delivery")) return "COD";
  return "Unknown";
}

function findMatch(re, text) {
  const m = text.match(re);
  return m ? m[1].trim() : null;
}

const SIZE_ORDER = { XS: 0, S: 1, M: 2, L: 3, XL: 4, XXL: 5, XXXL: 6 };
function sizeRank(s) {
  if (!s) return 999;
  const up = s.toUpperCase();
  if (up in SIZE_ORDER) return SIZE_ORDER[up];
  const n = parseFloat(s);
  return isNaN(n) ? 998 : 100 + n;
}

async function analyzeFile(pdfjs, bytes) {
  let doc;
  try {
    doc = await pdfjs.getDocument({ data: bytes.slice(), disableAutoFetch: true }).promise;
  } catch (e) {
    if (String(e).toLowerCase().includes("password"))
      throw new PdfInputError("This PDF is password-protected. Please remove the password and try again.");
    throw new PdfInputError("This file could not be read as a PDF.");
  }
  const pagesText = [];
  const pagesItems = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const items = content.items
      .map((it) => ({ s: (it.str || "").trim(), x: it.transform[4], y: it.transform[5] }))
      .filter((i) => i.s);
    pagesText.push(items.map((it) => it.s).join(" "));
    pagesItems.push(items);
  }
  return { numPages: doc.numPages, pagesText, pagesItems };
}

// Extract SKU / Size / Qty / Color / Order No from the Meesho "Product Details"
// table by column position (the SKU value can itself contain spaces).
function extractMeeshoProduct(items) {
  const orderHeader = items.find((i) => /order no/i.test(i.s));
  if (!orderHeader) return {};
  const hy = orderHeader.y;
  const colX = (name) => {
    const it = items.find((i) => i.s.toLowerCase() === name && Math.abs(i.y - hy) < 4);
    return it ? it.x : null;
  };
  const xSize = colX("size") ?? 203;
  const xQty = colX("qty") ?? 298;
  const xColor = colX("color") ?? 345;
  const xOrder = orderHeader.x;
  // value row: just below the header row
  const row = items.filter((i) => i.y < hy - 2 && i.y > hy - 32).sort((a, b) => a.x - b.x);
  const joinIn = (lo, hi) => row.filter((i) => i.x >= lo && i.x < hi).map((i) => i.s).join(" ").trim() || null;
  return {
    sku: joinIn(0, xSize),
    size: joinIn(xSize, xQty),
    qty: joinIn(xQty, xColor),
    color: joinIn(xColor, xOrder),
    orderNo: joinIn(xOrder, Infinity),
  };
}

async function loadPdfLib(bytes) {
  try {
    return await PDFDocument.load(bytes.slice(), { ignoreEncryption: false });
  } catch (e) {
    if (String(e).toLowerCase().includes("encrypt") || String(e).toLowerCase().includes("password"))
      throw new PdfInputError("This PDF is password-protected. Please remove the password and try again.");
    throw new PdfInputError("This file could not be read as a PDF.");
  }
}

// Build the list of orders from a single file.
async function buildOrders(pdfjs, file, forcedMarket) {
  const { numPages, pagesText, pagesItems } = await analyzeFile(pdfjs, file.bytes);
  const fullText = pagesText.join(" ");
  const market = forcedMarket || file.market || detectMarket(fullText);
  if (!market) {
    throw new PdfInputError(`"${file.name}" is not a recognised Amazon, Flipkart or Meesho label PDF.`);
  }
  if (forcedMarket) {
    const detected = detectMarket(fullText);
    // detected 'flipkart' also covers Shopsy (Shopsy uses the Flipkart layout).
    const ok = detected === forcedMarket;
    if (!ok) {
      const names = { amazon: "Amazon", flipkart: "Flipkart or Shopsy", meesho: "Meesho" };
      throw new PdfInputError(`"${file.name}" does not look like a ${names[forcedMarket] || forcedMarket} label PDF. Please upload the correct marketplace's label file.`);
    }
  }

  const srcDoc = await loadPdfLib(file.bytes);
  const orders = [];

  if (market === "amazon") {
    // Pages come in pairs: label page, then its invoice page.
    for (let i = 0; i < numPages; i += 2) {
      const invIdx = i + 1 < numPages ? i + 1 : null;
      const invText = invIdx != null ? pagesText[invIdx] : pagesText[i];
      orders.push({
        file, srcDoc, market,
        labelPage: i,
        invoicePage: invIdx,
        courier: "Amazon Transport",
        payment: "Unknown",
        sku: findMatch(/SKU[:\s#]+([A-Za-z0-9\-\_\/]+)/i, invText),
        qty: parseInt(findMatch(/(?:Qty|Quantity)[:\s]+(\d+)/i, invText) || "1", 10),
        color: null, size: null,
      });
    }
  } else {
    // Flipkart / Meesho: one A4 page per order (label + invoice on same page).
    for (let i = 0; i < numPages; i++) {
      const text = pagesText[i];
      let prod = {};
      let labelBottom = null;
      if (market === "meesho") {
        const items = pagesItems[i] || [];
        prod = extractMeeshoProduct(items);
        // Anchor the crop bottom just below the "TAX INVOICE" divider bar so the
        // label keeps that bar and drops the invoice table (matches reference).
        const taxItem = items.find((it) => /tax invoice/i.test(it.s));
        if (taxItem) labelBottom = taxItem.y - 5;
      }
      orders.push({
        file, srcDoc, market,
        labelPage: i,
        invoicePage: i,
        labelBottom,
        courier: findCourier(text) || "Unknown",
        payment: findPayment(text),
        sku: prod.sku || findMatch(/SKU[:\s#]*([A-Za-z0-9\-\_\/]+)/i, text),
        qty: parseInt(prod.qty || findMatch(/(?:Qty|Quantity)[:\s]+(\d+)/i, text) || "1", 10),
        color: prod.color || findMatch(/colou?r[:\s]+([A-Za-z]+)/i, text),
        size: market === "meesho" ? (prod.size || findMatch(/size[:\s]+([A-Za-z0-9 ]+)/i, text)) : null,
        orderNo: prod.orderNo || findMatch(/order\s*(?:no|id|#)[:\s]*([A-Za-z0-9\-_]+)/i, text),
      });
    }
  }
  return orders;
}

function clipBox(pageW, pageH, market, part) {
  // returns {left, bottom, right, top} in PDF coords (origin bottom-left)
  const region = (REGION[market] || REGION.flipkart)[part] || REGION.flipkart.label;
  return {
    left: pageW * region.l,
    bottom: pageH * region.b,
    right: pageW * region.r,
    top: pageH * region.t,
  };
}

async function embedRegion(outDoc, order, part) {
  const srcPage = order.srcDoc.getPage(part === "invoice" && order.invoicePage != null ? order.invoicePage : order.labelPage);
  const { width, height } = srcPage.getSize();
  const market = order.market;
  let box;
  if (market === "amazon") {
    box = { left: 0, bottom: 0, right: width, top: height };
  } else if (market === "meesho" && order.labelBottom != null) {
    // Dynamic Meesho crop anchored to the TAX INVOICE divider bar.
    box = part === "label"
      ? { left: 0, bottom: order.labelBottom, right: width, top: height }
      : { left: 0, bottom: 0, right: width, top: order.labelBottom };
  } else {
    box = clipBox(width, height, market, part);
  }
  const embedded = await outDoc.embedPage(srcPage, box);
  return { embedded, w: box.right - box.left, h: box.top - box.bottom };
}

function drawContain(page, embedded, ew, eh, x, y, boxW, boxH, allowRotate) {
  let rotate = false;
  let dw = ew, dh = eh;
  if (allowRotate) {
    const fitNormal = Math.min(boxW / ew, boxH / eh);
    const fitRot = Math.min(boxW / eh, boxH / ew);
    if (fitRot > fitNormal) rotate = true;
  }
  if (!rotate) {
    const scale = Math.min(boxW / ew, boxH / eh);
    dw = ew * scale; dh = eh * scale;
    page.drawPage(embedded, {
      x: x + (boxW - dw) / 2,
      y: y + (boxH - dh) / 2,
      width: dw, height: dh,
    });
  } else {
    const scale = Math.min(boxW / eh, boxH / ew);
    dw = eh * scale; dh = ew * scale; // rotated footprint
    // rotate 90deg CCW; anchor so it stays inside cell
    const cx = x + (boxW - dw) / 2;
    const cy = y + (boxH - dh) / 2;
    page.drawPage(embedded, {
      x: cx + dw,
      y: cy,
      width: ew * scale,
      height: eh * scale,
      rotate: degrees(90),
    });
  }
}

function drawDashedRect(page, x, y, w, h) {
  const dash = { dashArray: [4, 4], thickness: 0.6, color: rgb(0.6, 0.6, 0.6) };
  page.drawLine({ start: { x, y }, end: { x: x + w, y }, ...dash });
  page.drawLine({ start: { x, y: y + h }, end: { x: x + w, y: y + h }, ...dash });
  page.drawLine({ start: { x, y }, end: { x, y: y + h }, ...dash });
  page.drawLine({ start: { x: x + w, y }, end: { x: x + w, y: y + h }, ...dash });
}

function buildSummaryData(orders) {
  const skuMap = new Map();
  const cpMap = new Map();
  for (const o of orders) {
    const sku = o.sku || "(no SKU)";
    skuMap.set(sku, (skuMap.get(sku) || 0) + (o.qty || 1));
    const key = `${o.courier}|${o.payment}`;
    cpMap.set(key, (cpMap.get(key) || 0) + 1);
  }
  const skus = [...skuMap.entries()].map(([sku, qty]) => ({ sku, qty }));
  const couriers = [...cpMap.entries()].map(([k, count]) => {
    const [courier, payment] = k.split("|");
    return { courier, payment, count };
  });
  return { skus, couriers, total: orders.length };
}

function sortOrders(orders, opts) {
  let list = [...orders];
  const groupKey = (o) => {
    switch (opts.groupBy) {
      case "marketplace": return o.market;
      case "courier": return o.courier;
      case "payment": return o.payment;
      case "courier-payment": return `${o.courier} · ${o.payment}`;
      default: return "";
    }
  };
  const cmp = (a, b) => {
    const ga = groupKey(a), gb = groupKey(b);
    if (ga !== gb) return ga < gb ? -1 : 1;
    if (opts.sortVariant && opts.sortVariant !== "none") {
      const meeshoA = a.market === "meesho", meeshoB = b.market === "meesho";
      if (meeshoA !== meeshoB) return meeshoA ? -1 : 1;
      if (meeshoA && meeshoB) {
        const primary = opts.sortVariant === "color-size" ? "color" : "size";
        const pa = (a[primary] || "").toString(), pb = (b[primary] || "").toString();
        if (primary === "size") {
          const r = sizeRank(pa) - sizeRank(pb); if (r) return r;
        } else if (pa !== pb) return pa < pb ? -1 : 1;
        const sec = primary === "color" ? "size" : "color";
        if (sec === "size") { const r = sizeRank(a.size) - sizeRank(b.size); if (r) return r; }
        else { const sa = a.color || "", sb = b.color || ""; if (sa !== sb) return sa < sb ? -1 : 1; }
      }
    }
    if (opts.sortBySku) {
      const sa = a.sku || "", sb = b.sku || "";
      if (sa !== sb) return sa < sb ? -1 : 1;
    }
    return 0;
  };
  list.sort(cmp);
  return list;
}

async function renderSummaryPages(outDoc, orders, opts, font, fontBold) {
  const data = buildSummaryData(orders);
  const pages = [];
  const [W, H] = A4;
  let page = outDoc.addPage(A4);
  let y = H - 50;
  const line = (txt, size = 11, bold = false) => {
    if (y < 50) { page = outDoc.addPage(A4); y = H - 50; }
    page.drawText(txt, { x: 40, y, size, font: bold ? fontBold : font, color: rgb(0.1, 0.12, 0.18) });
    y -= size + 6;
  };
  line("Picking Summary", 18, true); y -= 6;
  line(`Total orders: ${data.total}`, 12, true); y -= 4;
  line("Units per SKU", 13, true);
  for (const s of data.skus) line(`${s.sku}  —  ${s.qty} unit(s)`);
  y -= 8;
  line("Parcels by courier & payment", 13, true);
  for (const c of data.couriers) line(`${c.courier} · ${c.payment}  —  ${c.count}`);
  if (opts.summaryOrderList) {
    y -= 8;
    line("Order list", 13, true);
    line("No.   Order        SKU            Qty  Courier        Payment", 9, true);
    orders.forEach((o) => {
      const row = `${String(o.__num ?? "").padEnd(5)} ${String(o.orderNo || "-").slice(0, 12).padEnd(12)} ${String(o.sku || "-").slice(0, 14).padEnd(14)} ${String(o.qty || 1).padEnd(4)} ${String(o.courier).slice(0, 14).padEnd(14)} ${o.payment}`;
      line(row, 9);
    });
  }
  return pages;
}

function labelDisplayNumber(o, opts) {
  if (!opts.numberOrders) return null;
  const prefix = (opts.orderNumberPrefix || "").slice(0, 10);
  return `${prefix}${o.__num}`;
}

async function renderOutput(orders, opts) {
  const outDoc = await PDFDocument.create();
  const font = await outDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await outDoc.embedFont(StandardFonts.HelveticaBold);

  const pageSize = resolvePageSize(opts.pageSize);
  const layout = opts.layout || "default";

  // Numbering (already in final print order)
  if (opts.numberOrders) {
    let n = parseInt(opts.orderNumberStart ?? 1, 10);
    orders.forEach((o) => { o.__num = n++; });
  }

  // Summary "first"
  if (opts.summary === "first") await renderSummaryPages(outDoc, orders, opts, font, fontBold);

  const stampMargin = (page, W) => {
    const bits = [];
    if (opts.customText) bits.push(opts.customText.slice(0, 60));
    if (opts.printDateTime) bits.push(new Date().toLocaleString());
    if (bits.length) {
      page.drawText(bits.join("   |   "), { x: 12, y: 10, size: 8, font, color: rgb(0.35, 0.35, 0.4) });
    }
  };

  if (opts.invoicesOnly) {
    for (const o of orders) {
      const { embedded, w, h } = await embedRegion(outDoc, o, "invoice");
      const size = pageSize || (o.market === "amazon" ? [w, h] : [w, h]);
      const page = outDoc.addPage(size);
      drawContain(page, embedded, w, h, 0, 0, size[0], size[1], !!opts.rotate);
      const num = labelDisplayNumber(o, opts);
      if (num) page.drawText(num, { x: size[0] - 60, y: size[1] - 24, size: 12, font: fontBold, color: rgb(0.15, 0.3, 0.85) });
      stampMargin(page, size[0]);
    }
  } else if (layout === "4up") {
    const [W, H] = A4;
    const cols = 2, rows = 2;
    const cw = W / cols, ch = H / rows;
    let idx = 0, page = null;
    for (const o of orders) {
      const cell = idx % 4;
      if (cell === 0) { page = outDoc.addPage(A4); }
      const c = cell % cols, r = Math.floor(cell / cols);
      const x = c * cw, y = H - (r + 1) * ch;
      drawDashedRect(page, x, y, cw, ch);
      const { embedded, w, h } = await embedRegion(outDoc, o, "label");
      const pad = 10;
      drawContain(page, embedded, w, h, x + pad, y + pad, cw - 2 * pad, ch - 2 * pad, true);
      if (opts.includeInvoice) { /* invoice added as extra pages below */ }
      const num = labelDisplayNumber(o, opts);
      if (num) page.drawText(num, { x: x + cw - 44, y: y + ch - 18, size: 11, font: fontBold, color: rgb(0.15, 0.3, 0.85) });
      idx++;
    }
    if (opts.includeInvoice) {
      for (const o of orders) {
        const { embedded, w, h } = await embedRegion(outDoc, o, "invoice");
        const p = outDoc.addPage(A4);
        drawContain(p, embedded, w, h, 20, 20, A4[0] - 40, A4[1] - 40, false);
      }
    }
  } else {
    for (const o of orders) {
      const { embedded, w, h } = await embedRegion(outDoc, o, "label");
      let size;
      let meeshoStampH = 0;
      if (pageSize) size = pageSize;
      else if (o.market === "flipkart") size = [900, 1500];
      else if (o.market === "meesho") {
        // Reserve a bottom strip for a bold SKU line ("SKU id name down side").
        meeshoStampH = 48;
        size = [900, Math.round(900 * (h / w)) + meeshoStampH];
      }
      else size = [w, h]; // amazon: keep original
      const allowRotate = !!pageSize && !!opts.rotate;

      if (opts.invoiceOnSamePage) {
        const p = outDoc.addPage(A4);
        const half = A4[1] / 2;
        drawContain(p, embedded, w, h, 15, half + 10, A4[0] - 30, half - 30, false);
        drawDashedRect(p, 10, half, A4[0] - 20, 0.1);
        p.drawLine({ start: { x: 10, y: half }, end: { x: A4[0] - 10, y: half }, dashArray: [5, 5], thickness: 0.8, color: rgb(0.6, 0.6, 0.6) });
        const inv = await embedRegion(outDoc, o, "invoice");
        drawContain(p, inv.embedded, inv.w, inv.h, 15, 10, A4[0] - 30, half - 30, false);
        const num = labelDisplayNumber(o, opts);
        if (num) p.drawText(num, { x: A4[0] - 60, y: A4[1] - 24, size: 12, font: fontBold, color: rgb(0.15, 0.3, 0.85) });
        stampMargin(p, A4[0]);
        continue;
      }

      const page = outDoc.addPage(size);
      if (!pageSize) {
        // Default per-market output: the crop region fills the page (above any stamp strip).
        page.drawPage(embedded, { x: 0, y: meeshoStampH, width: size[0], height: size[1] - meeshoStampH });
      } else {
        drawContain(page, embedded, w, h, 0, 0, size[0], size[1], allowRotate);
      }

      // Meesho: bold SKU line stamped on the reserved bottom strip.
      if (o.market === "meesho" && meeshoStampH) {
        const meta = [o.size && `Size: ${o.size}`, o.color && o.color !== "NA" && `Color: ${o.color}`, `Qty: ${o.qty || 1}`]
          .filter(Boolean).join("   ·   ");
        page.drawText(`SKU: ${o.sku || "-"}`, { x: 16, y: 28, size: 17, font: fontBold, color: rgb(0.05, 0.06, 0.1) });
        if (meta) page.drawText(meta, { x: 16, y: 12, size: 11, font, color: rgb(0.28, 0.3, 0.38) });
      }

      // Amazon: stamp SKU | Qty line
      if (o.market === "amazon") {
        const skuLine = `${o.sku || "SKU"} | Qty - ${o.qty || 1}`;
        page.drawText(skuLine, { x: 24, y: 24, size: 14, font: fontBold, color: rgb(0.05, 0.06, 0.1) });
      }
      const num = labelDisplayNumber(o, opts);
      if (num) page.drawText(num, { x: size[0] - 60, y: size[1] - 26, size: 13, font: fontBold, color: rgb(0.15, 0.3, 0.85) });
      stampMargin(page, size[0]);

      if (opts.includeInvoice && o.invoicePage != null) {
        const inv = await embedRegion(outDoc, o, "invoice");
        const p = outDoc.addPage(A4);
        drawContain(p, inv.embedded, inv.w, inv.h, 20, 20, A4[0] - 40, A4[1] - 40, false);
      }
    }
  }

  if (opts.summary === "last") await renderSummaryPages(outDoc, orders, opts, font, fontBold);

  const bytes = await outDoc.save();
  let summaryBytes;
  if (opts.summary === "separate") {
    const sDoc = await PDFDocument.create();
    const f = await sDoc.embedFont(StandardFonts.Helvetica);
    const fb = await sDoc.embedFont(StandardFonts.HelveticaBold);
    await renderSummaryPages(sDoc, orders, opts, f, fb);
    summaryBytes = await sDoc.save();
  }
  return { bytes, summaryBytes };
}

function validateConflicts(opts) {
  const thermal = typeof opts.pageSize === "string" || (opts.pageSize && opts.pageSize.widthMm);
  const fourUp = opts.layout === "4up";
  if (opts.invoiceOnSamePage && (thermal || fourUp))
    throw new PdfInputError("“Label and invoice on the same page” can’t be combined with a thermal size or 4-per-page.");
  if (thermal && fourUp)
    throw new PdfInputError("A thermal size can’t be combined with 4 labels per page.");
  if (opts.invoicesOnly && (opts.includeInvoice || opts.invoiceOnSamePage || thermal || fourUp))
    throw new PdfInputError("“Invoices only” can’t be combined with invoice or label-size options.");
}

export async function processFiles(files, opts = {}, pdfjs) {
  validateConflicts(opts);
  let allOrders = [];
  for (const file of files) {
    const orders = await buildOrders(pdfjs, file, opts.__forceMarket);
    allOrders = allOrders.concat(orders);
  }
  if (!allOrders.length) throw new PdfInputError("No orders were found in the uploaded file(s).");

  const sorted = sortOrders(allOrders, opts);

  let skipped = 0;
  let working = sorted;
  if (opts.invoicesOnly) {
    working = sorted.filter((o) => o.invoicePage != null);
    skipped = sorted.length - working.length;
  }

  const { bytes, summaryBytes } = await renderOutput(working, opts);
  const summary = buildSummaryData(working);
  const orders = working.map((o) => ({
    number: o.__num, orderNo: o.orderNo, sku: o.sku, qty: o.qty,
    courier: o.courier, payment: o.payment, market: o.market,
  }));
  const outPages = summary.total; // approximate page count for display
  return { bytes, summaryBytes, labels: working.length, pages: outPages, summary, orders, skipped };
}

export function cropAmazon(bytes, opts, pdfjs) {
  return processFiles([{ name: "amazon.pdf", bytes }], { ...opts, __forceMarket: "amazon" }, pdfjs);
}
export function cropFlipkart(bytes, opts, pdfjs) {
  return processFiles([{ name: "flipkart.pdf", bytes }], { ...opts, __forceMarket: "flipkart" }, pdfjs);
}
export function cropMeesho(bytes, opts, pdfjs) {
  return processFiles([{ name: "meesho.pdf", bytes }], { ...opts, __forceMarket: "meesho" }, pdfjs);
}
